import { apiGetTask } from "../../../network/api";
import { RESPONSE_STATUS } from "../../../utils/constants";
import { constructPayload, getPathPrefix } from "../../../utils/helper";
import { hourHeight } from "../calendar";
import { ReactComponent as TodoIcon } from "./../../../assets/icons/lsq_todo.svg";
import { ReactComponent as AppointmentIcon } from "./../../../assets/icons/lsq_appointment.svg";
import "./helper.css";
import {
  cacheExpirationTime,
  enableCache,
  getCache,
  updateCache,
} from "../../../hooks/helpers";
import analytics from "../../../utils/analytics";

const formatDateKey = (date) => {
  return date.toISOString().split("T")[0];
};

const formatTaskData = (calendarData) =>
  (calendarData?.taskList || [])
    .sort((a, b) => {
      const dateA = new Date(a.auditDetails?.dueDateLocalTime);
      const dateB = new Date(b.auditDetails?.dueDateLocalTime);
      return dateA - dateB;
    })
    .map(
      ({
        name,
        auditDetails,
        relatedEntity,
        category,
        color,
        userTaskAutoId,
        status,
      }) => ({
        userTaskAutoId,
        name,
        color,
        category,
        auditDetails: {
          dueDateLocalTime: auditDetails?.dueDateLocalTime,
          endDateLocalTime: auditDetails?.endDateLocalTime,
        },
        status: { status: status?.status },
        relatedEntity: {
          relatedEntityIdName: relatedEntity?.relatedEntityIdName,
          relatedEntityId: relatedEntity?.relatedEntityId,
          relatedSubEntityIsOpportunity:
            relatedEntity?.relatedSubEntityIsOpportunity,
          relatedSubEntityName: relatedEntity?.relatedSubEntityName,
          relatedSubEntityId: relatedEntity?.relatedSubEntityId,
          relatedSubEntityEvent: relatedEntity?.relatedSubEntityEvent,
          relatedEntityType: relatedEntity?.relatedEntityType,
        },
      }),
    );
function getIsDate(selectedDate) {
  const today = new Date();

  // Normalize both dates to remove time component
  const selected = new Date(selectedDate);
  selected.setHours(0, 0, 0, 0);

  today.setHours(0, 0, 0, 0);

  return selected.getTime() === today.getTime();
}
export const fetchCachedData = (activeDashboardId, widget, forceRefresh) => {
  const { id, updatedAt } = widget;
  const counterCache = getCache();
  const widgetCache = counterCache?.[activeDashboardId]?.[id];
  const currentTime = Date.now();
  if (
    enableCache &&
    !forceRefresh &&
    widgetCache?.timestamp &&
    widgetCache.data &&
    currentTime - widgetCache.timestamp < cacheExpirationTime &&
    new Date(updatedAt) < new Date(widgetCache.timestamp)
  ) {
    return widgetCache.data;
  }
  return null;
};
export const fetchCalendarData = async (
  selectedDate,
  setTasks,
  activeDashboardId,
  widget,
  forceRefresh,
) => {
  const cachedData = fetchCachedData(activeDashboardId, widget, forceRefresh);
  const isToday = getIsDate(selectedDate);
  if (isToday && cachedData)
    return setTasks({
      responseStatus: RESPONSE_STATUS.requestFullfilled,
      response: cachedData,
    });
  setTasks({
    responseStatus: RESPONSE_STATUS.requestPending,
  });
  let postBody = constructPayload({ config: widget.config });
  postBody.filters = {
    ...postBody.filters,
    from: formatDateKey(selectedDate),
    to: formatDateKey(selectedDate),
  };
  const calendarData = await apiGetTask(postBody);
  const response = formatTaskData(calendarData);
  setTasks(
    calendarData
      ? {
          responseStatus: RESPONSE_STATUS.requestFullfilled,
          response,
        }
      : { responseStatus: RESPONSE_STATUS.requestRejected },
  );
  if (isToday) updateCache(activeDashboardId, widget.id, response);
};

export const handleTaskNameClick = (event) => {
  let newUrl = new URL(window.location.href);
  if (event?.userTaskAutoId) {
    newUrl.pathname = getPathPrefix("Tasks");
    newUrl.searchParams.set("activeTab", "manage-tasks");
    newUrl.searchParams.set("taskAutoId", event?.userTaskAutoId);
    newUrl.searchParams.set(
      "leadType",
      event?.relatedEntity?.relatedEntityType,
    );
  }
  window.open(newUrl.toString(), "_blank");

  const analyticsData = {
    category: "widget_click",
    type: "click",
  };
  analytics.sendEvent("calendarview task card clicked", analyticsData);
};

export const handleLeadIdClick = (event) => {
  let newUrl = new URL(window.location.href);
  if (event?.relatedEntity?.relatedEntityId) {
    newUrl.pathname = `${getPathPrefix("Lead")}/LeadDetails`;
    newUrl.searchParams.delete("activeTab", "manage-tasks");
    newUrl.searchParams.set("LeadID", event?.relatedEntity?.relatedEntityId);
  }
  window.open(newUrl.toString(), "_blank");

  const analyticsData = {
    category: "widget_click",
    type: "click",
  };
  analytics.sendEvent(
    "calendarview task card - lead entity clicked",
    analyticsData,
  );
};

export const handleOpportunityIdClick = (event) => {
  let newUrl = new URL(window.location.href);
  if (event?.relatedEntity?.relatedEntityId) {
    newUrl.pathname = `${getPathPrefix("Opportunity")}/OpportunityDetails`;
    newUrl.searchParams.set("activeTab", "Tasks");
    newUrl.searchParams.set(
      "opportunityId",
      event?.relatedEntity?.relatedSubEntityId,
    );
    newUrl.searchParams.set(
      "opportunityEvent",
      event?.relatedEntity?.relatedSubEntityEvent,
    );
  }
  window.open(newUrl.toString(), "_blank");

  const analyticsData = {
    category: "widget_click",
    type: "click",
  };
  analytics.sendEvent(
    "calendarview task card - opportunity entity clicked",
    analyticsData,
  );
};

export const getEventPosition = (event) => {
  const dueDate = new Date(event.auditDetails?.dueDateLocalTime);
  const endDate = new Date(event.auditDetails?.endDateLocalTime);
  const startPosition =
    dueDate.getHours() * hourHeight + (dueDate.getMinutes() / 60) * hourHeight;
  const endPosition =
    endDate.getHours() * hourHeight + (endDate.getMinutes() / 60) * hourHeight;
  return { startPosition, endPosition };
};

export const getEventPositionTimeLine = (event, selectedDate) => {
  const dueDate = new Date(event.auditDetails?.dueDateLocalTime);
  let endDate = new Date(event.auditDetails?.endDateLocalTime);
  const timeDifference = endDate.getTime() - dueDate.getTime();
  if (timeDifference < 15 * 60 * 1000)
    endDate = new Date(dueDate.getTime() + 15 * 60 * 1000);
  const selectedDateStartOfDay = new Date(selectedDate).setHours(0, 0, 0, 0);
  const dueDateStartOfDay = new Date(dueDate).setHours(0, 0, 0, 0);
  const endDateStartOfDay = new Date(endDate).setHours(0, 0, 0, 0);
  let startPosition = 0;
  if (selectedDateStartOfDay <= dueDateStartOfDay) {
    startPosition =
      dueDate.getHours() * hourHeight +
      (dueDate.getMinutes() / 60) * hourHeight;
  }
  let endPosition = 0;
  if (selectedDateStartOfDay < endDateStartOfDay) {
    endPosition = 24 * hourHeight;
  } else if (selectedDateStartOfDay === endDateStartOfDay) {
    endPosition =
      endDate.getHours() * hourHeight +
      (endDate.getMinutes() / 60) * hourHeight;
  }
  return { startPosition, endPosition };
};

export const TaskIcon = ({ event = {} }) => {
  const isTodo = event.category === 1;
  const eventColor = event.color || "rgb(var(--ng-grey-50))";
  const Icon = isTodo ? TodoIcon : AppointmentIcon;

  // Convert hex to rgba with 0.15 opacity
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div
      role="task-icon"
      className="calander-task-icon text-xs"
      style={{ backgroundColor: hexToRgba(eventColor, 0.3) }}
    >
      <Icon className="calander-task-icon-svg" style={{ color: eventColor }} />
    </div>
  );
};

export const scrollToCurrentTime = (smooth, timelineRef) => {
  if (timelineRef.current) {
    const now = new Date();
    const currentTimePosition = getEventPosition({
      auditDetails: { dueDateLocalTime: now },
    });
    const containerHeight = timelineRef.current.clientHeight;
    const scrollPosition = Math.max(
      0,
      currentTimePosition?.startPosition - containerHeight / 2,
    );

    if (smooth) {
      timelineRef.current.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
    } else {
      timelineRef.current.scrollTop = scrollPosition;
    }
  }
};
const parseDate = (dateStr) => new Date(dateStr);

export const getOverlaptasks = (events) => {
  let maxOverlapCount = 0;
  const updatedTasks = events.map((task, index, arr) => {
    let position;
    const startTime = parseDate(task.auditDetails?.dueDateLocalTime);
    const endTime = parseDate(task.auditDetails?.endDateLocalTime);
    let overlapCount = 0;
    arr.forEach((otherTask, i) => {
      if (i !== index) {
        const otherStartTime = parseDate(
          otherTask.auditDetails?.dueDateLocalTime,
        );
        const otherEndTime = parseDate(
          otherTask.auditDetails?.endDateLocalTime,
        );
        if (startTime < otherEndTime && endTime > otherStartTime) {
          overlapCount++;
        }
      } else position = overlapCount;
    });
    overlapCount++;
    maxOverlapCount =
      maxOverlapCount > overlapCount ? maxOverlapCount : overlapCount;
    return {
      ...task,
      overlapCount,
      position,
    };
  });
  return [updatedTasks, maxOverlapCount];
};
